import "./style.scss";
import { FC } from "react";
import { AOPMaltaInvoiceDEData } from "@/src/types/admissionOffice";
import { getInvoiceDoubleLangRegistry, type InvoiceDoubleLangCountry, type InvoiceSecondLangCode } from "../invoiceLetterDoubleLangRegistry";
import { getAdmissionOfficePreviewBackground } from "@/src/utils/admissionOffice/getAdmissionOfficePreviewBackground";
import { isMaltaUzbRussianSignature, MALTA_UZB_RUSSIAN_SIGNATURE } from "@/src/constants/AdmissionOffice/Malta/AOMaltaUzbConsts";

interface AOPPrevProps {
  country: InvoiceDoubleLangCountry;
  secondLang: InvoiceSecondLangCode;
  bodyForPrev: AOPMaltaInvoiceDEData;
  withQR?: boolean;
  selectedSignature?: string;
}

const AOPPrev: FC<AOPPrevProps> = ({
  country,
  secondLang,
  bodyForPrev,
  withQR = true,
  selectedSignature = "Fedorova Anzhelika",
}) => {
  const { labels } = getInvoiceDoubleLangRegistry(country, secondLang);
  const actualBg = getAdmissionOfficePreviewBackground(country, withQR);
  const isUzb = secondLang === "uzb";
  const showRussianPreview = isUzb && isMaltaUzbRussianSignature(selectedSignature);
  const showEnglishPreview = !isUzb || !showRussianPreview;
  const showSecondLangPreview = !isUzb || showRussianPreview;
  const englishSignatureName =
    selectedSignature === "Fedorova Anzhelika Non Ukr" ? "Fedorova Anzhelika" : selectedSignature || "Fedorova Anzhelika";
  const secondLangSignatureName = isUzb ? MALTA_UZB_RUSSIAN_SIGNATURE : "Fedorova Anzhelika";

  const secondLangNationality = isUzb ? bodyForPrev.nationality : bodyForPrev.nationalitySecondLanguage;
  const secondLangCourse = isUzb ? bodyForPrev.course : bodyForPrev.courseSecondLanguage;
  const secondLangMedium = isUzb ? bodyForPrev.mediumOfInstruction : bodyForPrev.mediumOfInstructionSecondLanguage;
  const secondLangDuration = isUzb ? bodyForPrev.duration : bodyForPrev.durationSecondLanguage;
  const secondLangInvoiceBody = isUzb ? bodyForPrev.invoiceBody : bodyForPrev.invoiceBodySecondLanguage;

  return (
    <div className="admission-office-prev-wrapper">
      {showSecondLangPreview ? (
        <div className={`admission-office-prev ${country} ${secondLang} invoice`}>
          <img className="admission-office-prev-background" src={actualBg} alt="" />
          <div className="admission-office-prev-section title">{labels.invoiceTitle}</div>
          <div className="admission-office-prev-section number-and-date">
            <span>№ -</span>
            <span>
              {labels.datePrefix}: {bodyForPrev.currentDate ? bodyForPrev.currentDate : "-"}
            </span>
          </div>
          <div className="admission-office-prev-section info">
            <p>
              {labels.fullName}: {bodyForPrev.fullName || ""}
            </p>
            <p>
              {labels.birthDate}: {bodyForPrev.dateOfBirth || ""}
            </p>
            <p>
              {labels.nationality}: {secondLangNationality || ""}
            </p>
            <p>
              {labels.course}: {secondLangCourse || ""}
            </p>
            <p>
              {labels.medium}: {secondLangMedium || ""}
            </p>
            <p>
              {labels.duration}: {secondLangDuration || ""}
            </p>
          </div>
          <div className="admission-office-prev-section signature">
            <p>{labels.signatureTop}</p>
            <p>{secondLangSignatureName}</p>
            <p>{labels.signatureBottom}</p>
          </div>
          {secondLangInvoiceBody?.length ? (
            <div className="admission-office-prev-section body-content">
              {secondLangInvoiceBody.map((item, idx) => (
                <div key={idx} dangerouslySetInnerHTML={{ __html: item }} />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {showEnglishPreview ? (
        <div className={`admission-office-prev ${country} invoice`}>
          <img className="admission-office-prev-background" src={actualBg} alt="" />
          <div className="admission-office-prev-section title">INVOICE</div>
          <div className="admission-office-prev-section number-and-date">
            <span>№ -</span>
            <span>Date: {bodyForPrev.currentDate ? bodyForPrev.currentDate : "-"}</span>
          </div>
          <div className="admission-office-prev-section info">
            <p>Full Name: {bodyForPrev.fullName || ""}</p>
            <p>Data of Birth: {bodyForPrev.dateOfBirth || ""}</p>
            <p>Nationality: {bodyForPrev.nationality || ""}</p>
            <p>Course: {bodyForPrev.course || ""}</p>
            <p>Medium of Instruction: {bodyForPrev.mediumOfInstruction || ""}</p>
            <p>Duration: {bodyForPrev.duration || ""}</p>
          </div>
          <div className="admission-office-prev-section signature">
            <p>Head of Foreign Students department</p>
            <p>{isUzb ? englishSignatureName : "Fedorova Anzhelika"}</p>
            <p>International European University</p>
          </div>
          {bodyForPrev.invoiceBody?.length ? (
            <div className="admission-office-prev-section body-content">
              {bodyForPrev.invoiceBody.map((item, idx) => (
                <div key={idx} dangerouslySetInnerHTML={{ __html: item }} />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default AOPPrev;
